import type { BadgeTone } from '@/lib/utils/badgeTone'

import { formatDate } from './format'
import type { OperationVerificationView } from './operationVerificationTypes'

/**
 * 운영 인증 상태 요약·완료·실패 화면 전용 표현 매핑(설계 §2). `if (badge === ...)`를 페이지에 흩지
 * 않는다(`dashboardViewModel.ts`·`reservationSettingViewModel.ts`와 같은 컨벤션).
 *
 * 🚨 배지·기한·최종 확인 시점·사유는 전부 서버가 내려주는 값을 그대로 쓴다(재계산 금지) — 다만
 * FAQ·완료 화면 변경사항 도입부처럼 서버에 대응 필드가 없는 교육용 정적 카피는 상수로 둔다(설계 §2
 * 자율 결정 8, `VenueOperationVerificationPage.tsx`의 `DEADLINE_WARNING` 관례와 동일).
 */

/** 요구사항의 "인증 필요 상태(미신청·실패·미완료)" — `badge` 5상태 중 이 3가지만 해당한다. */
export function needsVerificationAction(badge: string): boolean {
  return badge === 'NOT_SUBMITTED' || badge === 'REJECTED' || badge === 'EXPIRED'
}

/** `#782` 인증 기한 임박 안내 화면의 "임박" 변형 — 인증이 유효하면서 기한이 임박한 조합만 해당한다. */
export function isVerificationExpiringSoon(view: OperationVerificationView): boolean {
  return view.badge === 'VERIFIED' && view.expiringSoon
}

const OPERATION_VERIFICATION_BADGE_TONE: Record<string, BadgeTone> = {
  NOT_SUBMITTED: 'neutral',
  UNDER_REVIEW: 'warning',
  VERIFIED: 'success',
  REJECTED: 'danger',
  EXPIRED: 'danger',
}

export interface OperationVerificationSummaryViewModel {
  badge: string
  badgeLabel: string
  tone: BadgeTone
  needsAction: boolean
  verifiedUntilText: string
  verifiedAtText: string
  exposureText: string
}

/** 대시보드·구장정보 요약 카드, 완료·실패 화면 상단 공용 매핑. */
export function toOperationVerificationSummaryViewModel(
  view: OperationVerificationView
): OperationVerificationSummaryViewModel {
  return {
    badge: view.badge,
    badgeLabel: view.badgeLabel,
    tone: OPERATION_VERIFICATION_BADGE_TONE[view.badge] ?? 'neutral',
    needsAction: needsVerificationAction(view.badge),
    verifiedUntilText: formatDate(view.verifiedUntil),
    verifiedAtText: formatDate(view.verifiedAt),
    // 🚨 `visibilityBlockReasonViews.isEmpty() ⟺ userVisible === true`(api-spec §2-1 불변식) — 두 값을
    // 조합해 다시 판정하지 않는다. 노출 중이면 라벨을, 아니면 대표 사유(첫 항목) 라벨을 보여준다.
    exposureText: view.userVisible ? '노출 중' : (view.visibilityBlockReasonViews[0]?.label ?? '노출 중단'),
  }
}

/**
 * 실패·미완료 화면의 사유 — 반려(`rejection`)가 있으면 서버 반려 사유 원문 그대로, 없으면(=순수
 * 기한 만료, `EXPIRED`이면서 반려 이력이 없는 경우) 고정 안내로 폴백한다.
 * 🚨 두 경로를 섞지 않는다 — `rejection.reason`을 대신할 서버 필드가 만료에는 없다.
 */
export function resolveFailureReason(view: OperationVerificationView): string {
  if (view.rejection) return view.rejection.reason
  return '운영 인증 기한이 지나 인증이 만료됐어요. 다시 인증해 주세요.'
}

/**
 * 완료 화면 "무엇이 달라지나요" 목록(요구사항 4). "검색 결과 노출"·"온라인 예약 활성화 가능"은
 * 서버 값(`userVisible`·`newOnlineReservationAvailable`)으로 조건부 문구를 고르고, "자동 정산 대상
 * 포함"만 정적 카피다(설계 §2 자율 결정 6 — EP 응답에 정산 대상 여부 필드가 없다).
 */
export function toCompletionChangeNotes(view: OperationVerificationView): string[] {
  return [
    view.userVisible ? '구장이 검색 결과에 노출돼요' : '노출 조건이 모두 충족되면 검색 결과에 노출돼요',
    view.newOnlineReservationAvailable
      ? '온라인 예약 기능을 활성화할 수 있어요'
      : '예약 기능을 켜면 온라인 예약을 받을 수 있어요',
    '자동 정산 대상에 포함돼요',
  ]
}

/**
 * 실패·미완료 화면 "지금 어떤 영향이 있나요" 목록(요구사항 5). 🚨 이슈 원문 문구를 고정 문자열로
 * 박지 않는다 — `REJECTED` 배지라도 직전 인증이 유효하면 계속 노출 중일 수 있다(api-spec §0-①
 * "반려됐지만 아직 노출 중"). 서버가 내려주는 `visibilityBlockReasonViews`·`newOnlineReservationAvailable`
 * 그대로 반영해 거짓 안내를 막는다(설계 §2 자율 결정 5).
 */
export function toFailureImpactNotes(view: OperationVerificationView): string[] {
  const exposureNotice = view.userVisible
    ? '현재 인증이 유효해 구장은 계속 노출돼요'
    : (view.visibilityBlockReasonViews[0]?.notice ?? '구장이 목록·검색 결과에서 제외돼요')

  const reservationNotice = view.newOnlineReservationAvailable
    ? '기존 인증이 유효해 온라인 예약은 계속 받을 수 있어요'
    : '신규 온라인 예약은 받을 수 없어요'

  return [exposureNotice, reservationNotice, '구장 정보와 관리자 권한은 그대로 유지돼요', '이미 확정된 예약과 정산은 계속 진행돼요']
}

/**
 * 인증 기한 임박 안내 화면(`#782`, Figma 14039-40006)의 "임박" 변형 영향 목록 — 아직 아무것도
 * 차단되지 않은 상태(`visibilityBlockReasonViews=[]`)라 "기한이 지나면 벌어질 일"이라는 미래형
 * 서술을 서버 값으로 파생할 수 없다. 이슈 원문 그대로 정적 카피로 둔다(설계 §2 자율 결정 3,
 * `#778`의 FAQ·완료 화면 관례와 같은 원칙). 🚨 "기한 초과" 변형은 이 상수를 쓰지 않는다 — 지금
 * 실제로 벌어진 일이라 `toFailureImpactNotes(view)`(서버 파생값)를 그대로 재사용한다.
 */
export const OPERATION_VERIFICATION_EXPIRY_WARNING_NOTES: string[] = [
  '기한이 지나면 목록·검색 결과에서 제외돼요',
  '기한이 지나면 신규 온라인 예약을 받을 수 없어요',
  '구장 정보와 관리자 데이터는 그대로 유지돼요',
]

export interface OperationVerificationFaqItem {
  question: string
  answer: string
}

/** 요구사항이 명시한 FAQ 2건(요구사항 5) — 서버에 대응 필드가 없는 정적 안내(설계 §2 자율 결정 8). */
export const OPERATION_VERIFICATION_FAQ: OperationVerificationFaqItem[] = [
  {
    question: '운영 인증이란 무엇인가요?',
    answer:
      '구장이 실제로 운영되고 있음을 사업자 정보·서류로 확인하는 절차예요. 인증을 완료하면 구장이 사용자 목록·검색 결과에 노출되고 온라인 예약을 받을 수 있어요.',
  },
  {
    question: '인증 후에도 예약 기능을 켤 수 있나요?',
    answer: '네, 인증 완료 후에는 언제든 예약 기능 설정 화면에서 온라인 예약을 켜고 끌 수 있어요.',
  },
]

/** 이력 목록 행의 처리 시점 표시 — 심사 중이면 날짜 대신 "심사 중"을 명시한다. */
export function operationVerificationReviewedAtText(reviewedAt: string | null): string {
  return reviewedAt ? formatDate(reviewedAt) : '심사 중'
}
